import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography } from 'app/components/ui';
import * as yup from 'yup';

interface EditableAccuracyInputProps {
  value: number | 'INF' | null;
  onSubmit: (value: number | 'INF' | null) => void;
  helperLabel?: string;
  validationSchema: yup.AnyObjectSchema;
  fieldName: string;
  placeholder?: string;
  disabled?: boolean;
}

export const EditableAccuracyInput = ({
  value,
  onSubmit,
  helperLabel,
  validationSchema,
  fieldName,
  placeholder,
  disabled = false,
}: EditableAccuracyInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { [fieldName]: value === 'INF' ? 'INF' : value ?? '' },
  });

  // Sync form when value changes externally
  useEffect(() => {
    setValue(fieldName, value === 'INF' ? 'INF' : value ?? '');
  }, [value, fieldName, setValue]);

  const doSubmit = handleSubmit((data) => {
    const raw = data[fieldName];
    if (raw === '' || raw === null || raw === undefined) {
      onSubmit(null);
    } else if (String(raw).toUpperCase() === 'INF') {
      onSubmit('INF');
    } else {
      onSubmit(Number(raw));
    }
  });

  const handleCancel = () => {
    reset({ [fieldName]: value === 'INF' ? 'INF' : value ?? '' });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
      inputRef.current?.blur();
    }
  };

  const { ref: formRef, ...registerProps } = register(fieldName);
  const fieldError = errors[fieldName];

  return (
    <div className="editable-number-input-wrapper">
      <input
        {...registerProps}
        ref={(e) => {
          formRef(e);
          inputRef.current = e;
        }}
        type="text"
        placeholder={placeholder}
        className={`editable-number-input${fieldError ? ' editable-number-input--error' : ''}`}
        onBlur={doSubmit}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {helperLabel && (
        <Typography variant="body-3" className="helper-label">
          {helperLabel}
        </Typography>
      )}
      {fieldError && (
        <Typography variant="body-3" color="#d93025" className="validation-error">
          {fieldError.message as string}
        </Typography>
      )}
    </div>
  );
};
